import React, { useRef } from "react";
import { Dialog, DialogContent, Box, IconButton } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { ServiceContent } from "../../../new-reports/types/newReportTypes";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ServiceContentView, {
  ServiceContentLabels,
} from "../common/ServiceContentView";
import XIconSvg from "../../../../common/svgs/XIconSvg";

/**
 * One prescribed service in a dialog.
 *
 * The body is `common/ServiceContentView`, shared with the History detail page
 * so the two cannot drift again — this component is now the dialog chrome
 * around it: the paper, the close button and the scroll container.
 */
type Props = {
  open: boolean;
  onClose: () => void;
  service: ServiceContent | null;
  labels: ServiceContentLabels & {
    specialInstruction?: string;
    contraindications?: string;
    preparationRequirements?: string;
  };
  unitType: number;
};

const ServiceViewModal = ({
  open,
  onClose,
  service,
  labels,
  unitType,
}: Props) => {
  const theme: Theme = useTheme();
  const scrollableRef = useRef<HTMLDivElement>(null);

  if (!service) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: styles.dialogPaper(theme),
        },
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        <IconButton onClick={onClose} sx={styles.closeButton}>
          <XIconSvg />
        </IconButton>

        <Box sx={styles.scrollContainer}>
          <ScrollableWrapper ref={scrollableRef}>
            <Box sx={styles.content}>
              <ServiceContentView
                service={service}
                labels={labels}
                unitType={unitType}
                // 700px of paper fits two columns of attributes.
                attributeColumns={2}
              />
            </Box>
          </ScrollableWrapper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceViewModal;

const styles = {
  dialogPaper: (theme: Theme) => ({
    borderRadius: "8px",
    maxWidth: "700px",
    maxHeight: "90vh",
    height: "90vh",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as "column",
  }),
  dialogContent: {
    padding: 0,
    position: "relative" as "relative",
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  closeButton: {
    position: "absolute" as "absolute",
    top: "16px",
    right: "16px",
    zIndex: 10,
    width: "40px",
    height: "40px",
  },
  scrollContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    // Right padding is short by the scrollbar's width; the close button needs
    // the top clearance.
    padding: "24px 14px 16px 24px",
  },
};
